/**
 * PlayerPanel — health/energy/deck indicator for one seat
 * (ui/31 §2/§3). Reads only public aggregate values from `PlayerState`, never
 * card contents (redaction already hid them upstream — ui/31 §4). Renders
 * nothing, sends no Action.
 */
import { useState, type CSSProperties, type HTMLAttributes, type ReactElement } from 'react';
import type { PlayerState } from '@cards/engine';
import type { DeckPassivePresentation } from '@cards/cards';
import { useTurnPlaybackSelector } from './TurnPlayback.js';
import { SeatGlyph } from './SeatGlyph.js';
import { PixelIcon } from './PixelIcon.js';
import { Badge, Button } from './kit/index.js';
import { useCountTween } from './useCountTween.js';
import { DeckPassiveDetails } from './DeckPassiveDetails.js';
import { CardInfoDialog, CardInfoPopover } from './CardInfoPopover.js';
import { deckPassiveInfoData } from './deckPassiveCard.js';
import { useDesktopHover } from './useDesktopHover.js';
import { FULL_CARD_HOVER_OPEN_DELAY_MS, useHoverPopover } from './useHoverPopover.js';
import { signedNumber } from './locale.js';

export interface PlayerPanelProps {
  player: PlayerState;
  /** true for the viewer's own panel (neutral), false for the opponent's (rival pink, 01 §2.3). */
  isViewer: boolean;
  testId: string;
  /** Public nickname; technical PlayerId is deliberately never rendered. */
  displayName?: string | undefined;
  /** Viewer-only spendable energy after subtracting the current staged plan. */
  spendableEnergy?: number;
  /** Whether to show the ordinary-token death counter. */
  showTokenDeaths?: boolean;
  /** Opens the viewer-only remaining-deck browser. Never provided for the opponent. */
  onDeckOpen?: () => void;
  /** Public from match start; derived only from the player-scoped persistent effect. */
  deckPassive?: DeckPassivePresentation | undefined;
  /** Hide deck metadata on editing surfaces such as Sandbox. */
  showMeta?: boolean;
  /**
   * Editing surfaces (the lab sandbox) make the passive itself editable, so its
   * badge doubles as a drag source: these props are spread onto the badge and
   * take it over from the read-only hover/tap preview.
   */
  passiveHandlers?: HTMLAttributes<HTMLDivElement> | undefined;
  /** Flight ghost still on its way into the slot — the badge waits blanked. */
  passiveHidden?: boolean;
  /** This seat pressed «Готов» in the current planning phase (02 §5.1). */
  ready?: boolean;
}

/** The energy row shows one coin per unit up to this many; above it, one coin and the number. */
const ENERGY_COIN_LIMIT = 6;

type EnergyCoinState = 'available' | 'reserved' | 'spent';

/**
 * The viewer's energy as coins (02 §5.1): what the plan can still spend, what
 * the staged plan already holds, and what this round has used up, against the
 * larger of the pool and its cap.
 */
function energyCoins(player: PlayerState, spendable: number): EnergyCoinState[] {
  const { energy, energyMax } = player.resources;
  const available = Math.max(0, spendable);
  const reserved = Math.max(0, energy - available);
  const spent = Math.max(0, Math.max(energyMax, energy) - available - reserved);
  return [
    ...Array<EnergyCoinState>(available).fill('available'),
    ...Array<EnergyCoinState>(reserved).fill('reserved'),
    ...Array<EnergyCoinState>(spent).fill('spent'),
  ];
}

export function PlayerPanel({ player, isViewer, testId, displayName, spendableEnergy, showTokenDeaths = false, onDeckOpen, deckPassive, showMeta = true, passiveHandlers, passiveHidden = false, ready = false }: PlayerPanelProps): ReactElement {
  // Per-seat playback slice: the panel re-renders only when a cue starts or
  // stops targeting this player.
  const { hackCandidate, hackSelected, combatImpact, effectImpact, effectHeal, healDelta, skipChanged, drawSequence, passiveVariant, passiveDelta } = useTurnPlaybackSelector((visuals) => ({
    hackCandidate: visuals.hackCue?.candidates.some(
      (candidate) => candidate.target.kind === 'player' && candidate.target.playerId === player.id,
    ) === true,
    hackSelected: visuals.hackCue?.selected?.target.kind === 'player'
      && visuals.hackCue.selected.target.playerId === player.id,
    combatImpact: visuals.combatCue?.phase === 'impact'
      && (visuals.combatCue.targetPlayerId === player.id
        || visuals.combatCue.reciprocal?.targetPlayerId === player.id),
    // A heal is not a blow (03 §4.12): only a hostile effect shakes the panel;
    // a positive one bounces the health chip instead.
    effectImpact: visuals.effectCue?.targetPlayerId === player.id && visuals.effectCue.phase !== 'positive',
    effectHeal: visuals.effectCue?.targetPlayerId === player.id && visuals.effectCue.phase === 'positive',
    skipChanged: visuals.skipChanges?.playerIds.includes(player.id) === true,
    healDelta: visuals.effectCue?.targetPlayerId === player.id && visuals.effectCue.phase === 'positive'
      ? visuals.effectCue.delta
      : undefined,
    drawSequence: visuals.drawCue?.playerId === player.id ? visuals.drawCue.sequence : undefined,
    passiveVariant: visuals.passivePulse?.playerId === player.id ? visuals.passivePulse.variant : undefined,
    passiveDelta: visuals.passivePulse?.playerId === player.id ? visuals.passivePulse.delta : undefined,
  }));
  const shownHealth = useCountTween(player.health);
  const shownEnergy = useCountTween(spendableEnergy ?? 0);
  const coins = spendableEnergy === undefined ? [] : energyCoins(player, spendableEnergy);
  const manyCoins = coins.length > ENERGY_COIN_LIMIT;
  // Past the limit the row folds to one lit coin beside the number.
  const shownCoins: EnergyCoinState[] = manyCoins ? ['available'] : coins;
  // Pulse the badge in the passive's own accent so the round-end trigger reads
  // as coming from it; the bright glyph tone doubles as the glow colour.
  const passiveGlow = deckPassive?.glyph.colors[2];
  const passiveDeltaLabel = passiveDelta === undefined || passiveDelta === 0
    ? undefined
    : signedNumber(passiveDelta);
  // On a hover-capable pointer the passive badge previews its full card, the
  // same interaction as hovering a board card in battle.
  const desktopHover = useDesktopHover();
  const {
    value: passiveHoverAnchor,
    closing: passiveHoverClosing,
    open: openPassiveHover,
    cancelClose: cancelPassiveHoverClose,
    scheduleClose: schedulePassiveHoverClose,
  } = useHoverPopover<HTMLElement>({ openDelayMs: FULL_CARD_HOVER_OPEN_DELAY_MS });
  // A badge that is being dragged owns its own pointer gestures: the sandbox
  // shows the card on hover itself, and a tap there means "pick the passive up".
  const passiveEditable = passiveHandlers !== undefined;
  const passivePreviewEnabled = deckPassive !== undefined && desktopHover && !passiveEditable;
  // Without a hover pointer the badge is tapped instead, and the description it
  // no longer has room to print arrives as the passive's full card — the same
  // gesture, and the same dialog, as tapping a card on the board.
  const passiveTapEnabled = deckPassive !== undefined && !desktopHover && !passiveEditable;
  const [passiveDetailsOpen, setPassiveDetailsOpen] = useState(false);
  return (
    <div
      className="cards-player-panel"
      data-testid={testId}
      data-onboarding={isViewer ? 'player-resources' : 'opponent-status'}
      data-player-id={player.id}
      data-combat-impact={combatImpact || effectImpact ? 'true' : undefined}
      data-player-heal={effectHeal ? 'true' : undefined}
      data-skip-changed={skipChanged ? 'true' : undefined}
      data-hack-candidate={hackCandidate ? 'true' : undefined}
      data-hack-selected={hackSelected ? 'true' : undefined}
      data-viewer={isViewer ? 'true' : 'false'}
    >
      <span className="cards-player-avatar" aria-hidden="true">
        <SeatGlyph seat={isViewer ? 'viewer' : 'opponent'} />
      </span>
      <div className="cards-player-content">
        <div className="cards-player-topline">
          <div className="cards-player-identity">
            {displayName === undefined || displayName.trim().length === 0 ? (
              <span className="cards-player-name-badge" data-empty="true">
                {isViewer ? 'ВЫ' : 'СОПЕРНИК'}
              </span>
            ) : (
              <span className="cards-player-name-badge">{displayName.trim()}</span>
            )}
            {deckPassive === undefined ? null : (
              <>
              <div
                className="cards-player-passive"
                data-passive-pulse={passiveVariant}
                data-passive-hidden={passiveHidden ? 'true' : undefined}
                style={passiveGlow === undefined
                  ? undefined
                  : ({ '--cards-passive-glow': passiveGlow } as CSSProperties)}
                role={passiveTapEnabled ? 'button' : undefined}
                tabIndex={passiveTapEnabled ? 0 : undefined}
                aria-haspopup={passiveTapEnabled ? 'dialog' : undefined}
                aria-label={passiveTapEnabled
                  ? `Пассивка колоды: ${deckPassive.name}. Открыть описание`
                  : undefined}
                onMouseEnter={passivePreviewEnabled ? (event) => openPassiveHover(event.currentTarget) : undefined}
                onMouseLeave={passivePreviewEnabled ? () => schedulePassiveHoverClose() : undefined}
                onClick={passiveTapEnabled ? () => setPassiveDetailsOpen(true) : undefined}
                onKeyDown={passiveTapEnabled
                  ? (event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    setPassiveDetailsOpen(true);
                  }
                  : undefined}
                {...passiveHandlers}
              >
                <DeckPassiveDetails
                  presentation={deckPassive}
                  density="compact"
                  testId={`${testId}-passive`}
                />
                {passiveDeltaLabel === undefined ? null : (
                  <span
                    className="cards-player-passive-delta"
                    data-testid={`${testId}-passive-delta`}
                    aria-hidden="true"
                  >
                    {passiveDeltaLabel}
                  </span>
                )}
                {passivePreviewEnabled && passiveHoverAnchor !== undefined ? (
                  <CardInfoPopover
                    anchor={passiveHoverAnchor}
                    data={deckPassiveInfoData(deckPassive)}
                    closing={passiveHoverClosing}
                    testId={`${testId}-passive-popover`}
                    onMouseEnter={cancelPassiveHoverClose}
                    onMouseLeave={schedulePassiveHoverClose}
                  />
                ) : null}
              </div>
              {/* Outside the badge, deliberately. The dialog is portalled to the
                  document, but a React portal still bubbles its events up the JSX
                  tree — nested in the badge, every click inside it (the close
                  button and the overlay included) reached the badge's own onClick
                  and re-opened what had just closed. Both board surfaces render
                  their dialog as a sibling for the same reason. */}
              {passiveTapEnabled && passiveDetailsOpen ? (
                <CardInfoDialog
                  data={deckPassiveInfoData(deckPassive)}
                  testId={`${testId}-passive-detail`}
                  onClose={() => setPassiveDetailsOpen(false)}
                />
              ) : null}
              </>
            )}
          </div>
          <div className="cards-player-stats">
            <div className="cards-player-health" aria-label={`Здоровье: ${player.health}`}>
              <span aria-hidden="true"><PixelIcon name="health" /> {shownHealth}</span>
              {healDelta !== undefined && healDelta !== 0 ? (
                <span className="cards-player-health-delta" aria-hidden="true">{signedNumber(healDelta)}</span>
              ) : null}
            </div>
            {isViewer && spendableEnergy !== undefined ? (
              <div
                className="cards-player-energy"
                data-testid={`${testId}-energy`}
                aria-label={`Доступная энергия: ${spendableEnergy}`}
                title="Энергия, доступная после подготовленных карт"
              >
                <span aria-hidden="true">
                  <span className="cards-player-energy-coins" data-many={manyCoins ? 'true' : undefined}>
                    {shownCoins.map((state, index) => (
                      <span key={index} className="cards-player-energy-coin" data-state={state} />
                    ))}
                  </span>
                  <span className="cards-player-energy-value">{shownEnergy}</span>
                </span>
              </div>
            ) : null}
            {/* The remaining-deck count rides the stat row next to health and
                energy: as a labelled row of its own it cost a second line of
                panel height, which the mobile battle spends on the board. The
                label survives as the accessible name and the tooltip. */}
            {!showMeta ? null : isViewer && onDeckOpen !== undefined ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="cards-player-stat cards-player-deck"
                data-testid={`${testId}-deck-count`}
                onClick={onDeckOpen}
                aria-label={`Ваша колода: ${player.deck.length}. Посмотреть оставшиеся карты`}
                title="Посмотреть оставшиеся карты в своей колоде"
              >
                {/* Keyed by the draw, so each card leaving the pile hops the count (03 §4.11). */}
                <span key={drawSequence ?? 'rest'} aria-hidden="true" data-drawn={drawSequence === undefined ? undefined : 'true'}>
                  <PixelIcon name="deck" /> {player.deck.length}
                </span>
              </Button>
            ) : (
              <div
                className="cards-player-stat cards-player-deck"
                data-testid={`${testId}-deck-count`}
                aria-label={`Колода соперника: ${player.deck.length}`}
              >
                <span key={drawSequence ?? 'rest'} aria-hidden="true" data-drawn={drawSequence === undefined ? undefined : 'true'}>
                  <PixelIcon name="deck" /> {player.deck.length}
                </span>
              </div>
            )}
            {ready ? (
              <Badge tone="warning" className="cards-player-ready" data-testid={`${testId}-ready`}>Готов</Badge>
            ) : null}
          </div>
        </div>
        <div className="cards-player-secondary">
          {showTokenDeaths ? (
            <Badge
              tone="neutral"
              className="cards-player-token-deaths"
              data-testid={`${testId}-token-deaths`}
            >
              Погибшие токены: {player.ordinaryTokenDeaths}
            </Badge>
          ) : null}
        </div>
      </div>
    </div>
  );
}
